/**
 * Data Center › PLM Contamination Check — laid out as the prototype
 * PLM-Contamination-Check-v2 (Ky answered 2026-10-14; rebuilt 2026-10-16).
 *
 * The lab runs a contamination check at least every 50 layers PER STATION (the
 * station's tools and media, whoever analyses). The check is ticked on the layer
 * (PLM Exam tab) and who / when / the station are stamped on save; the 51st
 * layer since the last check at a station cannot be saved unchecked.
 *
 * Top to bottom, as the prototype: the filter row (Period preset · From · To ·
 * Station · Analyst · Group by, and the rule), the CURRENT STATUS of each
 * station, then three sub-tabs — Summary (by month or quarter and station, with
 * a Total row), Check Log and Over-limit Layers. Every number is the server's
 * (PlmStationRules): the run is counted over ALL layers, so a filter never
 * resets a station's count, and Current status ignores the period and analyst.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { LookupCombo } from '../../../components/LookupCombo';
import { FormTitle } from '../../../components/FormTitle';
import { statusVariant, type StatusVariant } from '../../../components/statusPill';
import { fmtLocalDateTime } from '../../../utils/datetime';
import {
  fetchPlmContaminationReport, previewPlmContamination,
  type PlmContamCurrentStatus, type PlmContamLayerRow, type PlmContamPeriodSummary, type PlmContamReport,
} from './plm.api';
import {
  CONTAM_PERIODS, DEFAULT_CONTAM_PERIOD, contamCascade, contamPeriodRange, type ContamPeriod,
} from './plmContamination';
import './plm.css';

type Tab = 'sum' | 'log' | 'over';
const TABS: { id: Tab; label: string }[] = [
  { id: 'sum', label: 'Summary' },
  { id: 'log', label: 'Check Log' },
  { id: 'over', label: 'Over-limit Layers' },
];
type GroupBy = 'm' | 'q';
const GROUPS: { value: GroupBy; label: string }[] = [
  { value: 'm', label: 'Month' },
  { value: 'q', label: 'Quarter' },
];

/** A whole-number total (the prototype's Total row — "264", not "Sum=264.00"). */
const sumOf = <T,>(rows: readonly T[], f: (r: T) => number) => rows.reduce((a, r) => a + f(r), 0);

/** The shared status pill (statusPill.ts + the --ai-pill-* tokens), so a
 *  status reads the same colour here as in every other grid. */
function ContamPill({ text, variant }: { text: string; variant?: StatusVariant }) {
  return <span className={`adg-pill ${variant ?? statusVariant(text)}`}>{text}</span>;
}

/** "All" heads the Station / Analyst lists — picking it clears that filter. */
const ALL = 'All';
const lookupOf = (col: string, values: readonly string[], withAll = false) =>
  ({ cols: [col], rows: (withAll ? [ALL, ...values] : [...values]).map(v => [v]) });

/** One station's Current status (owner design 2026-10-16): last check, who, and
 *  the run since it against the limit as a bar. The numbers are the server's. */
function StationCard({ row, limit }: { row: PlmContamCurrentStatus; limit: number }) {
  const tone = statusVariant(row.status);
  const pct = Math.min(100, Math.round((row.sinceLastCheck / Math.max(1, limit)) * 100));
  return (
    <div className={`plm-ccq-card is-${tone}`}>
      <div className="plm-ccq-card-h">
        <b>{row.station}</b>
        <ContamPill text={row.status} />
      </div>
      <div className="plm-ccq-card-kv">
        <div><span>Last Check</span><b>{dt(row.lastCheckAt)}</b></div>
        <div><span>Checked By</span><b>{row.lastCheckedBy ?? '—'}</b></div>
      </div>
      <div className="plm-ccq-card-run">
        <span>Layers since last check</span>
        <span><b>{row.sinceLastCheck}</b> / {limit}</span>
      </div>
      <div className="plm-ccq-bar" role="progressbar" aria-valuemin={0} aria-valuemax={limit}
        aria-valuenow={row.sinceLastCheck} aria-label={`${row.station} layers since last check`}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const layerKey = (r: PlmContamLayerRow) => `${r.station}|${r.analyzedDate}|${r.sampleL ?? ''}`;
const dt = (v: string | null | undefined) => (v ? fmtLocalDateTime(v) : '—');

export default function PlmContaminationPage() {
  const initial = contamPeriodRange(DEFAULT_CONTAM_PERIOD, new Date())!;
  const [report, setReport] = useState<PlmContamReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>('sum');
  const [period, setPeriod] = useState<ContamPeriod>(DEFAULT_CONTAM_PERIOD);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [station, setStation] = useState('');
  const [analyst, setAnalyst] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('m');

  const [previewing, setPreviewing] = useState(false);
  /** Preview — PLM_ContaminationCheck_Summary for the filter on screen (prototype v2). */
  const preview = async () => {
    setPreviewing(true);
    try {
      await previewPlmContamination({ from, to, station, analyst, groupBy });
    } finally {
      setPreviewing(false);
    }
  };

  const load = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      setReport(await fetchPlmContaminationReport({ from, to, station, analyst, groupBy }));
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
  }, [from, to, station, analyst, groupBy]);
  useEffect(() => { void load(); }, [load]);

  const limit = report?.limit ?? 50;
  const cascade = contamCascade(
    report?.stationAnalysts ?? [], report?.stations ?? [], report?.analysts ?? [], station, analyst);
  const periodLabel = CONTAM_PERIODS.find(p => p.value === period)?.label ?? '';

  /** A preset fills From / To; typing a date switches the Period to Custom. */
  const pickPeriod = (p: ContamPeriod) => {
    setPeriod(p);
    const r = contamPeriodRange(p, new Date());
    if (r) { setFrom(r.from); setTo(r.to); }
  };
  const typeDate = (set: (v: string) => void) => (v: string) => { set(v); setPeriod('custom'); };

  const summaryCols = useMemo<AppColumn<PlmContamPeriodSummary>[]>(() => [
    { field: 'period', header: groupBy === 'q' ? 'Quarter' : 'Month', width: 90, text: r => r.period, summary: () => 'Total' },
    { field: 'station', header: 'Station', width: 110, text: r => r.station },
    { field: 'layers', header: 'Layers Analysed', width: 115, align: 'right', text: r => String(r.layers), summary: rows => String(sumOf(rows, r => r.layers)) },
    { field: 'checks', header: 'Checks Done', width: 100, align: 'right', text: r => String(r.checks), summary: rows => String(sumOf(rows, r => r.checks)) },
    {
      field: 'layersPerCheck', header: 'Layers per Check', width: 115, align: 'right',
      text: r => (r.layersPerCheck == null ? '—' : r.layersPerCheck.toFixed(1)),
      summary: rows => {
        const l = rows.reduce((a, r) => a + r.layers, 0);
        const c = rows.reduce((a, r) => a + r.checks, 0);
        return c ? (l / c).toFixed(1) : '—';
      },
    },
    {
      field: 'maxRun', header: 'Max Run', width: 80, align: 'right', text: r => String(r.maxRun),
      summary: rows => String(Math.max(0, ...rows.map(r => r.maxRun))),
    },
    {
      field: 'overLimit', header: 'Over-limit Layers', width: 120, align: 'right', text: r => String(r.overLimit),
      summary: rows => String(sumOf(rows, r => r.overLimit)),
    },
    { field: 'checkedBy', header: 'Checked By', width: 170, text: r => r.checkedBy || '—' },
    {
      field: 'compliant', header: 'Compliant', width: 90, text: r => (r.compliant ? 'Yes' : 'No'),
      render: r => <ContamPill text={r.compliant ? 'Yes' : 'No'} variant={r.compliant ? 'ok' : 'alert'} />,
      summary: rows => (rows.every(r => r.compliant) ? 'Yes' : 'No'),
    },
  ], [groupBy, limit]);

  const logCols = useMemo<AppColumn<PlmContamLayerRow>[]>(() => [
    { field: 'checkedDate', header: 'Checked At', width: 150, text: r => dt(r.checkedDate ?? r.analyzedDate) },
    { field: 'station', header: 'Station', width: 120, text: r => r.station },
    { field: 'checkedBy', header: 'Checked By', width: 150, text: r => r.checkedBy ?? r.analyst ?? '' },
    { field: 'jobId', header: 'Job ID', width: 120, text: r => r.jobId ?? '' },
    { field: 'sampleCode', header: 'Sample #', width: 150, text: r => r.sampleCode ?? '' },
    { field: 'sampleL', header: 'Layer', width: 170, text: r => r.sampleL ?? '' },
    {
      field: 'prevGap', header: 'Layers Since Previous Check', width: 200, align: 'right', text: r => String(r.prevGap),
    },
  ], [limit]);

  const overCols = useMemo<AppColumn<PlmContamLayerRow>[]>(() => [
    { field: 'analyzedDate', header: 'Analyzed At', width: 150, text: r => dt(r.analyzedDate) },
    { field: 'station', header: 'Station', width: 120, text: r => r.station },
    { field: 'analyst', header: 'Analyst', width: 150, text: r => r.analyst ?? '' },
    { field: 'jobId', header: 'Job ID', width: 120, text: r => r.jobId ?? '' },
    { field: 'sampleCode', header: 'Sample #', width: 150, text: r => r.sampleCode ?? '' },
    { field: 'sampleL', header: 'Layer', width: 170, text: r => r.sampleL ?? '' },
    {
      field: 'run', header: 'Layers Without Check', width: 170, align: 'right', text: r => String(r.run),
    },
  ], []);

  const summary = report?.summary ?? [];
  const log = report?.log ?? [];
  const over = report?.overLimit ?? [];

  const exportData = tab === 'sum'
    ? {
        filename: 'PLM-Contamination-Summary',
        headers: [groupBy === 'q' ? 'Quarter' : 'Month', 'Station', 'Layers Analysed', 'Checks Done',
          'Layers per Check', 'Max Run', 'Over-limit Layers', 'Checked By', 'Compliant'],
        rows: summary.map(r => [r.period, r.station, String(r.layers), String(r.checks),
          r.layersPerCheck == null ? '' : r.layersPerCheck.toFixed(1), String(r.maxRun), String(r.overLimit),
          r.checkedBy, r.compliant ? 'Yes' : 'No']),
      }
    : tab === 'log'
      ? {
          filename: 'PLM-Contamination-Check-Log',
          headers: ['Checked At', 'Station', 'Checked By', 'Job ID', 'Sample #', 'Layer', 'Layers Since Previous Check'],
          rows: log.map(r => [dt(r.checkedDate ?? r.analyzedDate), r.station, r.checkedBy ?? r.analyst ?? '',
            r.jobId ?? '', r.sampleCode ?? '', r.sampleL ?? '', String(r.prevGap)]),
        }
      : {
          filename: 'PLM-Contamination-Over-limit',
          headers: ['Analyzed At', 'Station', 'Analyst', 'Job ID', 'Sample #', 'Layer', 'Layers Without Check'],
          rows: over.map(r => [dt(r.analyzedDate), r.station, r.analyst ?? '', r.jobId ?? '',
            r.sampleCode ?? '', r.sampleL ?? '', String(r.run)]),
        };

  return (
    <div className="plm-root">
      <div className="dp-breadcrumb">
        <span>Home</span><i className="ti ti-chevron-right" />
        <span>Data Center</span><i className="ti ti-chevron-right" />
        <span className="dp-bc-active">PLM Contamination Check</span>
      </div>
      <FormTitle pageId="dc-plm-contamination" title="PLM Contamination Check" loadMs={loadMs} loading={loading} auditFormName="PLM" />

      <AppToolbar
        onRefresh={() => { void load(); }}
        refreshing={loading}
        exportData={exportData}
        onPreview={() => { void preview(); }}
        previewLabel="Preview"
        previewLast  /* prototype v2 bar: Refresh · Export to · Preview */
        previewDisabled={loading || previewing}
      />

      {/* Filter bar (owner design 2026-10-16) — uppercase label beside a compact
          Sample Registration box (shared LookupCombo, 26px). Station and Analyst
          cascade. Filters are not edits, so they never raise the unsaved prompt. */}
      <div className="plm-ccq-filters" data-unsaved-ignore>
        <label className="plm-ccq-f" htmlFor="plm-ccq-period">
          <span>Period</span>
          <span className="plm-ccq-box is-wide">
            <LookupCombo id="plm-ccq-period" className="plm-ccq-combo" popupMatchTrigger showFilter={false}
              lookup={lookupOf('Period', CONTAM_PERIODS.map(p => p.label))} value={periodLabel}
              onPick={r => pickPeriod(CONTAM_PERIODS.find(p => p.label === r[0])?.value ?? DEFAULT_CONTAM_PERIOD)} />
          </span>
        </label>
        <span className="plm-ccq-sep" aria-hidden="true" />
        <label className="plm-ccq-f" htmlFor="plm-ccq-from">
          <span>From</span>
          <input id="plm-ccq-from" type="date" className="plm-ccq-date" value={from} onChange={e => typeDate(setFrom)(e.target.value)} />
        </label>
        <label className="plm-ccq-f" htmlFor="plm-ccq-to">
          <span>To</span>
          <input id="plm-ccq-to" type="date" className="plm-ccq-date" value={to} onChange={e => typeDate(setTo)(e.target.value)} />
        </label>
        <span className="plm-ccq-sep" aria-hidden="true" />
        <label className="plm-ccq-f" htmlFor="plm-ccq-station">
          <span>Station</span>
          <span className="plm-ccq-box">
            <LookupCombo id="plm-ccq-station" className="plm-ccq-combo" popupMatchTrigger placeholder={ALL}
              lookup={lookupOf('Station', cascade.stations, true)} value={station}
              onPick={r => setStation(r[0] === ALL ? '' : (r[0] ?? ''))} />
          </span>
        </label>
        <label className="plm-ccq-f" htmlFor="plm-ccq-analyst">
          <span>Analyst</span>
          <span className="plm-ccq-box">
            <LookupCombo id="plm-ccq-analyst" className="plm-ccq-combo" popupMatchTrigger placeholder={ALL}
              lookup={lookupOf('Analyst', cascade.analysts, true)} value={analyst}
              onPick={r => setAnalyst(r[0] === ALL ? '' : (r[0] ?? ''))} />
          </span>
        </label>
        <label className="plm-ccq-f" htmlFor="plm-ccq-group">
          <span>Group by</span>
          <span className="plm-ccq-box is-narrow">
            <LookupCombo id="plm-ccq-group" className="plm-ccq-combo" popupMatchTrigger showFilter={false}
              lookup={lookupOf('Group by', GROUPS.map(g => g.label))} value={GROUPS.find(g => g.value === groupBy)?.label}
              onPick={r => setGroupBy(r[0] === 'Quarter' ? 'q' : 'm')} />
          </span>
        </label>
        <span className="plm-ccq-rule" title={`The ${limit + 1}st layer since the last check at a station cannot be saved unchecked.`}>
          <i className="ti ti-info-circle" aria-hidden="true" />
          Rule (Ky): &ge; 1 check every <b>{limit}</b> layers per station
        </span>
      </div>

      <h3 className="plm-ccq-h">Current status</h3>
      <div className="plm-ccq-cards">
        {(report?.current ?? []).map(c => <StationCard key={c.station} row={c} limit={limit} />)}
        {!loading && (report?.current ?? []).length === 0 && (
          <p className="plm-hint">No station is set up yet. Set the stations up in Settings › PLM Instrument Setup.</p>
        )}
      </div>

      <div className="plm-tabs plm-ccq-tabs" role="tablist" aria-label="Contamination view">
        {TABS.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
            className={`plm-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="plm-ccq-grid">
        {tab === 'sum' && (
          <>
            <AppDataGrid
              rows={summary}
              columns={summaryCols}
              getRowId={r => `${r.periodStart}|${r.station}`}
              loading={loading}
              defaultPageSize={500}
              hideFooter
              emptyText="No layers in this period."
            />
            <div className="plm-ccq-notes">
              <p><b>Max run</b> — the longest stretch of layers analysed without a check.</p>
              <p>
                <b>Over-limit layers</b> — the {limit + 1}st+ layer saved with no check. The PLM Exam now blocks that,
                so these come only from history before enforcement.
              </p>
              <p><b>Compliant</b> — a period is compliant when it has no overdue layers.</p>
            </div>
          </>
        )}
        {tab === 'log' && (
          <AppDataGrid
            rows={log}
            columns={logCols}
            getRowId={layerKey}
            loading={loading}
            defaultPageSize={50}
            emptyText="No checks in this period."
          />
        )}
        {tab === 'over' && (
          <AppDataGrid
            rows={over}
            columns={overCols}
            getRowId={layerKey}
            loading={loading}
            defaultPageSize={50}
            emptyText="No over-limit layers — compliant."
          />
        )}
      </div>
    </div>
  );
}
